/**
 * GOTY auto-fill picker — desktop twin of `sheets/GotyPickSheet.tsx` (G-07).
 *
 * NEW UI with no prototype behind it, so it is assembled from the existing
 * overlay vocabulary rather than ported: `Modal` at 720px — the tag picker's
 * width, and the right one here for the same reason, a single column of rows
 * that must not sprawl. Both trees read `gotyPickVM` verbatim; only geometry
 * differs (compact padding, two-column rows, no 44px touch floor), which is the
 * `tagPickerVM` split the codebase already uses for a shared picker.
 *
 * See the sheet's header for what the thing is FOR — the short version is that
 * Steam only sends play dates to the account owning the API key, so for every
 * member but one the auto-fill has to reason from other evidence and then ask.
 */

import { SearchBox } from '../../components/SearchBox.tsx';
import { useQd } from '../../store/index.ts';
import { gotyPickVM } from '../../store/selectors.ts';
import { Modal } from '../components/Modal.tsx';
import type { CSSProperties } from 'react';

const MONO = 'var(--gt-font-mono)';

/** Header notes are mono 10px `--gt-muted` in the prototype (§4). */
const NOTE: CSSProperties = { fontSize: 10, color: 'var(--gt-muted)' };

interface Row {
  key: string;
  appid: number;
  name: string;
  meta: string;
  on: boolean;
  mark: string;
  bd: string;
  clr: string;
  metaClr: string;
}

export default function GotyPickModal(): JSX.Element {
  const s = useQd();
  const v = gotyPickVM(s);

  const row = (r: Row): JSX.Element => (
    <div
      key={r.key}
      onClick={() => s.toggleGotyPick(r.appid)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        border: '1px solid ' + r.bd,
        background: 'var(--gt-tile)',
        padding: '8px 11px',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          fontFamily: MONO,
          fontSize: 11,
          width: 14,
          flex: 'none',
          color: r.on ? 'var(--acc)' : 'var(--gt-border-3)',
        }}
      >
        {r.on ? r.mark : '·'}
      </div>
      <div style={{ fontWeight: 600, fontSize: '12.5px', color: r.clr, flex: 1, minWidth: 0 }}>
        {r.name}
      </div>
      <div
        style={{
          fontFamily: MONO,
          fontSize: '8.5px',
          letterSpacing: '.06em',
          color: r.metaClr,
          flex: 'none',
        }}
      >
        {r.meta}
      </div>
    </div>
  );

  return (
    <Modal
      label="Goty pick modal"
      width={720}
      onClose={s.closeGotyPick}
      header={{ title: v.title, titleClr: 'var(--gt-text)', note: <span style={NOTE}>{v.note}</span> }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, padding: '15px 18px 18px' }}>
        {v.why.map((line, i) => (
          <div
            key={'why' + i}
            style={{ fontFamily: MONO, fontSize: '9.5px', lineHeight: 1.7, color: 'var(--gt-muted)' }}
          >
            {line}
          </div>
        ))}

        {v.hasRows ? (
          v.rows.map(row)
        ) : (
          <div style={{ fontFamily: MONO, fontSize: '9.5px', lineHeight: 1.7, color: 'var(--gt-muted-2)' }}>
            {v.emptyTxt}
          </div>
        )}

        {v.hasGuess ? (
          <div
            style={{
              fontFamily: MONO,
              fontSize: '9px',
              letterSpacing: '.03em',
              lineHeight: 1.7,
              color: 'var(--gt-ghost-2)',
            }}
          >
            {v.guessTxt}
          </div>
        ) : null}

        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            borderTop: '1px solid var(--gt-line)',
            paddingTop: 12,
          }}
        >
          <span
            style={{
              fontFamily: MONO,
              fontSize: '9px',
              letterSpacing: '.06em',
              color: 'var(--gt-faint)',
            }}
          >
            MISSING SOMETHING?
          </span>
          <SearchBox
            value={v.q}
            onChange={(e) => s.setGotyPickQ(e.target.value)}
            placeholder={v.searchPlaceholder}
            w="280px"
            ifs={10.5}
          />
        </div>

        {v.libRows.map(row)}

        {v.libNoHit ? (
          <div style={{ fontFamily: MONO, fontSize: '9.5px', lineHeight: 1.7, color: 'var(--gt-muted-2)' }}>
            {v.libNoHitTxt}
          </div>
        ) : null}

        <div
          onClick={() => {
            // A plain `div`, so there is no `disabled` doing this for us.
            if (v.canConfirm) void s.gotyPickConfirm();
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid ' + v.confirmBd,
            color: v.confirmClr,
            background: 'var(--gt-panel)',
            fontFamily: MONO,
            fontSize: '10.5px',
            letterSpacing: '.06em',
            padding: '11px 0',
            cursor: v.busy ? 'progress' : v.canConfirm ? 'pointer' : 'default',
          }}
        >
          {v.confirmLbl}
        </div>
      </div>
    </Modal>
  );
}
